import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";

import { makeStyles } from "tss-react/mui";

import { visuallyHidden } from "@mui/utils";

import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Divider from "@mui/material/Divider";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { ButtonUI, KMKDialog } from "../../Common";
import { convertTimezone, setPrevState } from "../../../util/common";
import { showToast } from "../../../store/actions";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root": {
      width: "100%",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiFormControl-root": {
      margin: 0,
    },
    "& .MuiSvgIcon-root": {
      verticalAlign: "middle",
    },
  },
  divider: {
    marginTop: "5px",
    marginBottom: "9px",
  },
  container: {
    padding: "24px 24px 0 24px",
    backgroundColor: "#FFFFFF",
  },
  bodyContainer: {
    width: "100%",
    marginLeft: "0",
    marginBottom: "0",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  searchContainer: {
    minWidth: "250px",
  },
  headerIcons: {
    marginRight: "10px",
  },
  innerDialog: {
    "& .MuiPaper-root": {
      width: "100%",
    },
  },
  tableContainer: {
    height: "312px",
    overflow: "auto",
  },
  TableHead: {
    "& th": {
      position: "sticky",
      top: "0px",
      zIndex: 2,
      backgroundColor: "rgb(255, 255, 255)",
    },
  },
  selectableRow: {
    cursor: "pointer",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.01)",
    },
  },
  selectedRow: {
    background: "rgba(0, 0, 0, 0.1)",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.1)",
    },
  },
  TablePagination: {
    width: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    "& .MuiToolbar-root": {
      width: "100%",
      "& p": {
        margin: 0,
      },
    },
  },
  FirstTableCell: {
    wordBreak: "break-all",
    display: "inline-block",
    width: "210px",
    verticalAlign: "middle",
  },
}));

//Headcells
const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
    sortable: true,
  },
  {
    id: "lastModified",
    numeric: false,
    disablePadding: false,
    label: "Last Modified On",
    sortable: true,
  },
  {
    id: "modifiedBy",
    numeric: false,
    disablePadding: false,
    label: "Last Modified By",
    sortable: true,
  },
  {
    id: "action",
    numeric: true,
    disablePadding: false,
    label: "Action",
    sortable: false,
  },
];

//Table Head with sorting
function TableHeadWithSorting(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, i) => (
          <TableCell
            style={i === 0 ? { width: "300px" } : {}}
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function TemplateBrowser({
  setOpenFileBrowser,
  note,
  getData,
  uploadFile,
  deleteClick,
  downloadClick,
  saveClick,
}) {
  const [browserState, setBrowserState] = useState({
    loading: false,
    search: "",
    rows: [],
    filteredRows: [],
    confirmDelete: false,
    deleteObject: {},
    order: "asc",
    orderBy: "name",
    page: 0,
    rowsPerPage: 5,
    selected: "",
  });

  const dispatch = useDispatch();
  const upload = useRef(null);
  const copyText = useRef(null);

  const { classes } = useStyles();

  //Getting file  data
  const getAllData = useCallback(async () => {
    setPrevState(setBrowserState, { search: "", page: 0, rows: [], filteredRows: [], loading: true });

    const response = await getData();
    //Checking if file path is correct or not
    if (response) {
      const rowData = response.map((data) => ({ ...data, checked: false }));
      setPrevState(setBrowserState, {
        rows: rowData,
        filteredRows: rowData,
      });
    } else {
      setOpenFileBrowser(false);
    }
    setPrevState(setBrowserState, { loading: false });
  }, [getData, setOpenFileBrowser]);

  //Download click
  const handleDownloadClick = async (id, name) => {
    setPrevState(setBrowserState, { loading: true });
    downloadClick(id, name);
    setPrevState(setBrowserState, { loading: false });
  };

  //Save click
  const handleSaveClick = () => {
    setPrevState(setBrowserState, { loading: true });
    saveClick(browserState.selected);
    setPrevState(setBrowserState, { loading: false });
  };

  //Creating delete object and opening delete confirmation
  const deleteConfirmation = (id, name) => {
    setPrevState(setBrowserState, {
      deleteObject: {
        message: `Are you sure you want to delete the template "${name}"?`,
        title: "Delete Template",
        name,
        id,
      },
      confirmDelete: true,
    });
  };

  //Delete click
  const handleDeleteClick = async () => {
    setPrevState(setBrowserState, { loading: true, selected: false });

    const response = await deleteClick(browserState.deleteObject.id);

    if (response.success) {
      dispatch(showToast({ message: response.message, type: "success" }));
      setPrevState(setBrowserState, { confirmDelete: false });
      await getAllData();
    } else {
      setPrevState(setBrowserState, { confirmDelete: false });
    }

    setPrevState(setBrowserState, { loading: false });
  };

  //Upload file
  const handleUploadFile = async (e) => {
    const uploadedFiles = e.target.files;

    if (uploadedFiles?.length) {
      setPrevState(setBrowserState, { loading: true });
      const response = await uploadFile({ uploadedFiles });

      if (response.success) {
        dispatch(showToast({ message: response.message, type: "success" }));
        await getAllData();
      }

      setPrevState(setBrowserState, { loading: false });
    }

    e.target.value = null;
  };

  //Close popup
  const handleCancel = () => {
    setOpenFileBrowser(false);
  };

  //Clear search
  const handleClearSearch = () => {
    setPrevState(setBrowserState, { search: "" });
  };

  useEffect(() => {
    //Creating Head cells array
    getAllData();

    return () => {
      setBrowserState({
        loading: false,
        search: "",
        rows: [],
        filteredRows: [],
        confirmDelete: false,
        deleteObject: {},
        order: "asc",
        orderBy: "name",
        page: 0,
        rowsPerPage: 5,
        selected: "",
      });
    };
  }, [getAllData]);

  useEffect(() => {
    setPrevState(setBrowserState, {
      page: 0,
      filteredRows: browserState.rows.filter((fileObj) =>
        fileObj.name.toLowerCase().includes(browserState.search.toLowerCase())
      ),
    });
  }, [browserState.search, browserState.rows]);

  //Comparing afor sorting
  function descendingComparator(a, b, orderBy) {
    if (orderBy === "lastModified") {
      if (new Date(b[orderBy]).getTime() < new Date(a[orderBy]).getTime()) {
        return -1;
      }
      if (new Date(b[orderBy]).getTime() > new Date(a[orderBy]).getTime()) {
        return 1;
      }
      return 0;
    } else {
      if (b[orderBy]?.toLowerCase() < a[orderBy]?.toLowerCase()) {
        return -1;
      } else if (b[orderBy]?.toLowerCase() > a[orderBy]?.toLowerCase()) {
        return 1;
      }
      return 0;
    }
  }

  //Comparing order
  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // This method is created for cross-browser compatibility, if you don't
  // need to support IE11, you can use Array.prototype.sort() directly
  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  //Sort cloumn click
  const handleRequestSort = (event, property) => {
    const isAsc = browserState.orderBy === property && browserState.order === "asc";
    setPrevState(setBrowserState, { order: isAsc ? "desc" : "asc", orderBy: property });
  };

  //next page click
  const handleChangePage = (event, newPage) => {
    setPrevState(setBrowserState, { page: newPage });
  };

  //rows per page dropdown click
  const handleChangeRowsPerPage = (event) => {
    setPrevState(setBrowserState, {
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
  };

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={browserState.loading}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog className={classes.dialogPaper} maxWidth="md" open={true} onClose={handleCancel}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar className={classes.Toolbar} variant="dense">
            Load from Template
          </Toolbar>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 4,
              color: "black",
              mr: "10px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </AppBar>
        <Box sx={{ width: "100%" }} className={classes.container}>
          <Backdrop
            sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={browserState.loading}
          >
            <CircularProgress color="primary" />
          </Backdrop>
          <Grid container spacing={0} sx={{ mb: 2 }} className={classes.bodyContainer}>
            <Grid
              sx={{ py: 2 }}
              justifyContent={"flex-start"}
              size={{
                xs: 6,
                md: 6
              }}>
              <input type="file" accept=".JSON" ref={upload} style={{ display: "none" }} onChange={handleUploadFile} />
              <ButtonUI
                color="info"
                className={classes.headerIcons}
                variant="contained"
                style={{ width: "auto" }}
                onClick={() => upload.current.click()}
              >
                Upload
              </ButtonUI>
            </Grid>
            <Grid
              sx={{ py: 2 }}
              justifyContent={"flex-end"}
              display={"flex"}
              size={{
                xs: 6,
                md: 6
              }}>
              <Box className={classes.searchContainer}>
                <TextField
                  margin="normal"
                  value={browserState.search}
                  onChange={(e) => setPrevState(setBrowserState, { search: e.target.value })}
                  placeholder="Search"
                  variant="standard"
                  fullWidth
                  InputProps={{
                    startAdornment: <SearchIcon color="primary" />,
                    ...(browserState.search && {
                      endAdornment: (
                        <IconButton onClick={handleClearSearch} size="small">
                          <ClearIcon color="primary" />
                        </IconButton>
                      ),
                    }),
                  }}
                />
              </Box>
            </Grid>
            <Divider className={classes.divider} />
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table stickyHeader sx={{ minWidth: 650 }} size="small">
                <TableHeadWithSorting
                  className={classes.TableHead}
                  order={browserState.order}
                  orderBy={browserState.orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {stableSort(browserState.filteredRows, getComparator(browserState.order, browserState.orderBy))
                    .slice(
                      browserState.page * browserState.rowsPerPage,
                      browserState.page * browserState.rowsPerPage + browserState.rowsPerPage
                    )
                    .map(({ id, name, lastModified, modifiedBy }) => (
                      <TableRow
                        key={id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                        // Add class(es) based on File Selection condition
                        className={`${`${classes.selectableRow} ${
                          name === browserState.selected ? classes.selectedRow : ""
                        }`}`}
                        onClick={() => {
                          // Set selected variable with filename selected
                          setPrevState(setBrowserState, { selected: browserState.selected ? false : name });
                        }}
                      >
                        <TableCell component="th" scope="row" sx={{ pl: 0 }}>
                          <InsertDriveFileIcon sx={{ mr: 1 }} />
                          <span className={classes.FirstTableCell}>{name}</span>
                        </TableCell>
                        <TableCell align="left">{lastModified ? convertTimezone(lastModified) : ""}</TableCell>
                        <TableCell align="left">{modifiedBy}</TableCell>

                        <TableCell align="right">
                          <IconButton color="info" onClick={() => handleDownloadClick(id, name)}>
                            <FileDownloadIcon />
                          </IconButton>
                          <IconButton color="error" onClick={() => deleteConfirmation(id, name)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className={classes.TablePagination}
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={browserState.filteredRows.length}
              rowsPerPage={browserState.rowsPerPage}
              page={browserState.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <Grid justifyContent={"flex-start"} size={12}>
              <Typography sx={{ color: (theme) => theme.palette.error.main }}>NOTE: {note}</Typography>
            </Grid>
          </Grid>
          <DialogActions sx={{ padding: "0 0 16px 0" }}>
            <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
              Cancel
            </ButtonUI>
            <ButtonUI
              onClick={() => {
                handleSaveClick();
              }}
              variant="contained"
              color="success"
              style={{ marginLeft: 15 }}
              disabled={browserState.loading || !browserState.selected}
            >
              Load
            </ButtonUI>
          </DialogActions>
        </Box>

        {browserState.confirmDelete && (
          <KMKDialog
            maxWidth="sm"
            open={true}
            onClose={() => setPrevState(setBrowserState, { confirmDelete: false })}
            className={classes.innerDialog}
          >
            <Backdrop
              sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={browserState.loading}
            >
              <CircularProgress color="primary" />
            </Backdrop>
            <AppBar sx={{ position: "relative" }}>
              <Toolbar className={classes.Toolbar}>{browserState.deleteObject.title}</Toolbar>
              <IconButton
                onClick={() => setPrevState(setBrowserState, { confirmDelete: false })}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "black",
                }}
              >
                <CloseIcon />
              </IconButton>
            </AppBar>

            <Box sx={{ width: "100%" }} className={classes.container}>
              <p style={{ marginBottom: 0 }}>{browserState.deleteObject.message}</p>
            </Box>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <ButtonUI
                variant="contained"
                color="secondary"
                onClick={() => setPrevState(setBrowserState, { confirmDelete: false })}
              >
                Cancel
              </ButtonUI>
              <ButtonUI
                onClick={handleDeleteClick}
                variant="contained"
                color="success"
                style={{ marginLeft: 15 }}
                disabled={browserState.loading}
              >
                Ok
              </ButtonUI>
            </DialogActions>
          </KMKDialog>
        )}
        <input type="hidden" ref={copyText} />
      </KMKDialog>
    </>
  );
}
